import { groupBy } from 'ramda'
import { keys } from 'ramda'
import React, { UIEvent } from 'react'
import styled from 'styled-components'
import { useUserState } from '~/atoms/UserState'
import Text, { FontStyle } from '~/components/core/Text'
import { light, spacing } from '~/constants/theme'
import { PendingMessage } from '~/pages/Messaging/hooks/usePendingMessages'
import { desktop } from '~/styles'
import { formatRelativeDay } from '~/utils/formatter'

import { formatTimeString } from '@mangovoice/core'
import { CallLogDirection, Message } from '@mangovoice/types'

import MessageBubble from './MessageBubble'
import MessageDateSplitter from './MessageDateSplitter'
import PendingMessageBubble from './PendingMessageBubble'

const RootWrapper = styled.div`
  width: 100%;
  overflow-y: scroll;
  display: flex;
  flex-direction: column-reverse;

  &::-webkit-scrollbar {
    width: ${desktop.scrollbar}px;
    background-color: transparent;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: ${desktop.scrollbar}px;
    -webkit-box-shadow: inset 0px 4px 14px rgba(0, 0, 0, 0.1);
    background-color: ${light.dark600};
  }
`

const MessageEndWrapper = styled.div`
  float: left;
  clear: both;
`

const MessageGroupWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column-reverse;
`

const MessageItemWrapper = styled.div`
  width: 100%;
  padding: ${spacing.XS} 0;
  box-sizing: border-box;
`

interface MessageTimeWrapperProps {
  self?: boolean
}
const MessageTimeWrapper = styled.div<MessageTimeWrapperProps>`
  width: 100%;
  height: 22px;
  padding-top: ${spacing.spacingLG};
  padding-bottom: ${spacing.spacingSM};
  display: flex;
  align-items: flex-end;
  justify-content: ${(props) => (props.self ? 'flex-end' : 'flex-start')};

  > p:nth-child(2) {
    margin-left: 4px;
  }
`

function isPendingMessage(
  value: Message | PendingMessage,
): value is PendingMessage {
  // Currently Message doesn't have a uuid and PendingMessage always does, so check if it has a uuid after casting
  return (value as PendingMessage).uuid !== undefined
}

interface MessageListProps {
  messages: (Message | PendingMessage)[]
  onScroll?: (e: UIEvent<HTMLDivElement>) => void
  onRetrySend?: (message: PendingMessage) => void
}
const MessageList = React.forwardRef<HTMLInputElement, MessageListProps>(
  (props, ref) => {
    const { messages, onScroll, onRetrySend } = props
    const { pbxUsers, sessionUser } = useUserState()

    const dateGroupedMessages = groupBy(
      (x) => formatRelativeDay(x.created_at),
      messages,
    )

    return (
      <RootWrapper onScroll={onScroll}>
        <MessageEndWrapper ref={ref} />
        {keys(dateGroupedMessages).map((dateKey) => {
          const messageList = dateGroupedMessages[dateKey]
          return (
            <div key={`message-group-${String(dateKey)}`}>
              <MessageDateSplitter date={dateKey} />
              <MessageGroupWrapper>
                {messageList.map((message, ind) => {
                  const { direction, sender_user_id: messageUserId } = message

                  const messageUserName =
                    pbxUsers?.find((user) => user.id === messageUserId)
                      ?.full_name ?? ''

                  const fromCurrentUser = messageUserId === sessionUser?.id

                  const isFromTeam = direction === CallLogDirection.Outbound

                  const messageTime = formatTimeString(message.created_at, {
                    hour12: true,
                  })
                  let showTime = true
                  if (messageList[ind + 1]) {
                    const nextMsg = messageList[ind + 1]
                    const nextTime = formatTimeString(nextMsg.created_at, {
                      hour12: true,
                    })
                    if (
                      message.sender_user_id === nextMsg.sender_user_id &&
                      messageTime === nextTime
                    )
                      showTime = false
                  }

                  const isPending = isPendingMessage(message)
                  const key = isPending ? `pending-${message.uuid}` : message.id

                  return (
                    <MessageItemWrapper key={`message-${key}`}>
                      {showTime && (
                        <MessageTimeWrapper self={isFromTeam}>
                          {isFromTeam && (
                            <Text
                              fontStyle={FontStyle.bTiny}
                              text={messageUserName}
                              color={light.dark900}
                            />
                          )}
                          <Text
                            fontStyle={FontStyle.Tiny}
                            text={messageTime}
                            color={light.dark900}
                          />
                        </MessageTimeWrapper>
                      )}

                      {isPending ? (
                        <PendingMessageBubble
                          message={message}
                          onRetry={onRetrySend}
                        />
                      ) : (
                        <MessageBubble
                          fromCurrentUser={fromCurrentUser}
                          isFromTeam={isFromTeam}
                          message={message}
                        />
                      )}
                    </MessageItemWrapper>
                  )
                })}
              </MessageGroupWrapper>
            </div>
          )
        })}
      </RootWrapper>
    )
  },
)

export default MessageList
